import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Button } from "./Button";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "./Formfield";
import { FileUpload } from "./FileUpload";
import CKEditorFull from "./CKEditor";
import { sanitizeChapterHtml } from "../Utils/sanitizeHtml";

import {
  Chapter,
  ChapterContent,
  ChapterRole,
  ContentType,
  Course
} from "../Types/CourseTypes";
import {
  AddChapter,
  AddChapterContent,
  DeleteChapter,
  DeleteChapterContent,
  UpdateChapter,
  UpdateChapterContent
} from "../DataService/CourseService";

interface ChapterFormProps {
  course: Course;
  onChapterAdded: (updatedCourse: Course) => void;
}

interface ChapterFormData {
  title: string;
  description: string;
  sortOrder: number;
  chapterRole: ChapterRole;
}

interface ContentFormData {
  contentType: ContentType;
  sortOrder: number;
  textContent: string;
}

const emptyChapterForm: ChapterFormData = {
  title: "",
  description: "",
  sortOrder: 1,
  chapterRole: ChapterRole.Normal
};

const isRotCourse = (course: Course) => {
  const category = String(course.category ?? "").trim().toLowerCase();
  return category === "rot" || category === "__system-rot";
};

const getSuggestedChapterRole = (course: Course, sortOrder: number) => {
  if (isRotCourse(course) && sortOrder === 1) return ChapterRole.Prep;
  return ChapterRole.Normal;
};

const getChapterRoleLabel = (role: ChapterRole) => {
  switch (role) {
    case ChapterRole.Prep:
      return "Uppstartsmodul";
    case ChapterRole.Main:
      return "Huvuddel";
    default:
      return "Standard";
  }
};

const emptyContentForm: ContentFormData = {
  contentType: ContentType.Text,
  sortOrder: 1,
  textContent: ""
};

const toMediaLabel = (contentType: ContentType) => {
  switch (contentType) {
    case ContentType.Video:
      return "Video";
    case ContentType.Attachment:
    case ContentType.Pdf:
      return "Dokument";
    case ContentType.Exercise:
      return "Övning";
    default:
      return "Text";
  }
};

export const ChapterForm: React.FC<ChapterFormProps> = ({ course, onChapterAdded }) => {
  const [displayCourse, setDisplayCourse] = useState(course);
  const [selectedChapterId, setSelectedChapterId] = useState<number | null>(null);
  const [editingChapterId, setEditingChapterId] = useState<number | null>(null);
  const [editingContentId, setEditingContentId] = useState<number | null>(null);
  const [chapterUploadResetToken, setChapterUploadResetToken] = useState(0);
  const [contentUploadResetToken, setContentUploadResetToken] = useState(0);
  const [isSavingChapter, setIsSavingChapter] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const activeChapterCardRef = useRef<HTMLElement | null>(null);

  const chapterForm = useForm<ChapterFormData>({ defaultValues: emptyChapterForm });
  const contentForm = useForm<ContentFormData>({ defaultValues: emptyContentForm });

  useEffect(() => {
    setDisplayCourse(course);
  }, [course]);

  const selectedChapter = useMemo(() => {
    return displayCourse.chapters.find((chapter) => chapter.id === selectedChapterId) ?? null;
  }, [displayCourse.chapters, selectedChapterId]);

  const sortedChapters = useMemo(() => {
    return [...displayCourse.chapters].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [displayCourse.chapters]);

  const editingChapter = useMemo(() => {
    return displayCourse.chapters.find((chapter) => chapter.id === editingChapterId) ?? null;
  }, [displayCourse.chapters, editingChapterId]);

  const editingContent = useMemo(() => {
    return selectedChapter?.contents.find((content) => content.id === editingContentId) ?? null;
  }, [editingContentId, selectedChapter]);

  useEffect(() => {
    setSelectedChapterId(null);
    setEditingChapterId(null);
    setEditingContentId(null);
    chapterForm.reset({ ...emptyChapterForm, chapterRole: getSuggestedChapterRole(course, 1) });
    contentForm.reset(emptyContentForm);
    setChapterUploadResetToken((prev) => prev + 1);
    setContentUploadResetToken((prev) => prev + 1);
  }, [course.id, chapterForm, contentForm]);

  useEffect(() => {
    if (editingChapter) {
      chapterForm.reset({
        title: editingChapter.title,
        description: editingChapter.description ?? "",
        sortOrder: editingChapter.sortOrder,
        chapterRole: editingChapter.chapterRole ?? ChapterRole.Normal
      });
      return;
    }

    chapterForm.reset({ ...emptyChapterForm, chapterRole: getSuggestedChapterRole(course, 1) });
  }, [editingChapter, chapterForm]);

  useEffect(() => {
    if (editingContent) {
      contentForm.reset({
        contentType: editingContent.contentType,
        sortOrder: editingContent.sortOrder,
        textContent: editingContent.textContent ?? ""
      });
      return;
    }

    contentForm.reset(emptyContentForm);
  }, [editingContent, contentForm]);

  useEffect(() => {
    setContentUploadResetToken((prev) => prev + 1);
  }, [selectedChapterId, editingContentId]);

  const watchedContentType = contentForm.watch("contentType");
  const watchedTextContent = contentForm.watch("textContent");
  const hasUnsavedChanges = chapterForm.formState.isDirty || contentForm.formState.isDirty;
  const textContentPreview = useMemo(() => {
    return sanitizeChapterHtml(watchedTextContent ?? "");
  }, [watchedTextContent]);
  const getContentSummary = (content: ChapterContent) => {
    if (content.contentType === ContentType.Text || content.contentType === ContentType.Exercise) {
      const text = sanitizeChapterHtml(content.textContent ?? "");
      if (!text) return "Inget textinnehåll";

      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");
      const plain = doc.body.textContent?.replace(/\s+/g, " ").trim() ?? "";
      return plain.length > 120 ? `${plain.slice(0, 120)}...` : plain;
    }

    return content.contentType === ContentType.Video
      ? content.video?.title || content.video?.fileName || "Video"
      : content.attachment?.fileName || "Dokument";
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const commitCourse = (updated: Course) => {
    setDisplayCourse(updated);
    onChapterAdded(updated);
    setChapterUploadResetToken((prev) => prev + 1);
    setContentUploadResetToken((prev) => prev + 1);
  };

  const removeChapterFromCourse = (chapterId: number) => {
    setDisplayCourse((prev) => ({
      ...prev,
      chapters: prev.chapters.filter((chapter) => chapter.id !== chapterId)
    }));
  };

  const removeContentFromCourse = (contentId: number) => {
    setDisplayCourse((prev) => ({
      ...prev,
      chapters: prev.chapters.map((chapter) => ({
        ...chapter,
        contents: chapter.contents.filter((content) => content.id !== contentId)
      }))
    }));
  };

  const saveChapter = async (data: ChapterFormData) => {
    setIsSavingChapter(true);
    try {
      const payload: Chapter = {
        id: editingChapter?.id ?? 0,
        courseId: displayCourse.id,
        title: data.title,
        description: data.description,
        sortOrder: data.sortOrder,
        chapterRole: data.chapterRole,
        contents: editingChapter?.contents ?? []
      };

      const updatedCourse = editingChapter
        ? await UpdateChapter(editingChapter.id, payload)
          : await AddChapter(displayCourse.id, {
            title: payload.title,
            description: payload.description,
            sortOrder: payload.sortOrder,
            chapterRole: payload.chapterRole,
            courseId: payload.courseId
          });

      if (!updatedCourse) return;

      commitCourse(updatedCourse);

      setEditingChapterId(null);
      chapterForm.reset(emptyChapterForm);
    } finally {
      setIsSavingChapter(false);
    }
  };

  const saveContent = async (data: ContentFormData) => {
    if (!selectedChapter) return;

    setIsSavingContent(true);
    try {
      const base: ChapterContent = {
        id: editingContent?.id ?? 0,
        chapterId: selectedChapter.id,
        contentType: data.contentType,
        sortOrder: data.sortOrder,
        textContent: data.textContent,
        videoId: editingContent?.videoId ?? editingContent?.video?.id ?? null,
        attachmentId: editingContent?.attachmentId ?? editingContent?.attachment?.id ?? null
      };

      const updatedCourse = editingContent
        ? await UpdateChapterContent(editingContent.id, base)
        : await AddChapterContent(selectedChapter.id, {
            chapterId: base.chapterId,
            contentType: base.contentType,
            sortOrder: base.sortOrder,
            textContent: base.textContent,
            videoId: base.videoId,
            attachmentId: base.attachmentId,
            video: base.video,
            attachment: base.attachment
          });

      if (!updatedCourse) return;

      commitCourse(updatedCourse);

      setEditingContentId(null);
      contentForm.reset(emptyContentForm);
    } finally {
      setIsSavingContent(false);
    }
  };

  const handleCreateVideoUploaded = async (videoDto: any) => {
    if (!selectedChapter) return;

    setIsSavingContent(true);
    try {
      const updatedCourse = await AddChapterContent(selectedChapter.id, {
        chapterId: selectedChapter.id,
        contentType: ContentType.Video,
        sortOrder: contentForm.getValues("sortOrder"),
        video: videoDto,
        textContent: contentForm.getValues("textContent")
      });

      if (!updatedCourse) return;

      commitCourse(updatedCourse);

      contentForm.reset(emptyContentForm);
    } finally {
      setIsSavingContent(false);
    }
  };

  const handleCreateDocumentUploaded = async (attachmentDto: any) => {
    if (!selectedChapter) return;

    setIsSavingContent(true);
    try {
      const updatedCourse = await AddChapterContent(selectedChapter.id, {
        chapterId: selectedChapter.id,
        contentType: ContentType.Attachment,
        sortOrder: contentForm.getValues("sortOrder"),
        attachment: attachmentDto,
        textContent: contentForm.getValues("textContent")
      });

      if (!updatedCourse) return;

      commitCourse(updatedCourse);

      contentForm.reset(emptyContentForm);
    } finally {
      setIsSavingContent(false);
    }
  };

  const handleEditVideoUploaded = async (videoDto: any) => {
    if (!selectedChapter || !editingContent) return;

    setIsSavingContent(true);
    try {
      const updatedCourse = await UpdateChapterContent(editingContent.id, {
        id: editingContent.id,
        chapterId: selectedChapter.id,
        contentType: ContentType.Video,
        sortOrder: contentForm.getValues("sortOrder"),
        textContent: contentForm.getValues("textContent"),
        videoId: videoDto.id,
        attachmentId: null
      });

      if (!updatedCourse) return;

      commitCourse(updatedCourse);

      setEditingContentId(null);
    } finally {
      setIsSavingContent(false);
    }
  };

  const handleEditDocumentUploaded = async (attachmentDto: any) => {
    if (!selectedChapter || !editingContent) return;

    setIsSavingContent(true);
    try {
      const updatedCourse = await UpdateChapterContent(editingContent.id, {
        id: editingContent.id,
        chapterId: selectedChapter.id,
        contentType: ContentType.Attachment,
        sortOrder: contentForm.getValues("sortOrder"),
        textContent: contentForm.getValues("textContent"),
        videoId: null,
        attachmentId: attachmentDto.id
      });

      if (!updatedCourse) return;

      commitCourse(updatedCourse);

      setEditingContentId(null);
    } finally {
      setIsSavingContent(false);
    }
  };

  const deleteChapter = async (chapterId: number) => {
    if (!window.confirm("Ta bort kapitlet och allt dess innehåll?")) return;

    const success = await DeleteChapter(chapterId);
    if (!success) return;

    if (selectedChapterId === chapterId) setSelectedChapterId(null);
    if (editingChapterId === chapterId) setEditingChapterId(null);
    removeChapterFromCourse(chapterId);
  };

  const deleteContent = async (contentId: number) => {
    if (!window.confirm("Ta bort innehållet?")) return;

    const success = await DeleteChapterContent(contentId);
    if (!success) return;

    if (editingContentId === contentId) setEditingContentId(null);
    removeContentFromCourse(contentId);
  };

  const confirmDiscardChanges = () => {
    if (!hasUnsavedChanges) return true;

    return window.confirm("Du har osparade ändringar. Vill du fortsätta ändå?");
  };

  const openChapter = (chapterId: number) => {
    if (selectedChapterId === chapterId) {
      if (!confirmDiscardChanges()) return;
      setSelectedChapterId(null);
      setEditingContentId(null);
      return;
    }

    if (!confirmDiscardChanges()) return;

    setSelectedChapterId(chapterId);
    setEditingContentId(null);
  };

  const contentEditor = selectedChapter && (
    <section className="chapter-editor__content-card chapter-editor__content-card--inline">
      <div className="chapter-editor__section-head">
        <div>
          <h3>{editingContent ? "Redigera innehåll" : `Innehåll för ${selectedChapter.title}`}</h3>
          <p>{editingContent ? "Justera sortering, typ eller text" : "Lägg till video, dokument eller text i det valda kapitlet"}</p>
        </div>

        {editingContent && (
          <Button
            text="Avbryt innehållsredigering"
            className="chapter-editor__secondary-btn"
            onClick={() => setEditingContentId(null)}
          />
        )}
      </div>

      <FormProvider {...contentForm}>
        <form onSubmit={contentForm.handleSubmit(saveContent)} className="chapter-editor__form chapter-editor__form--content">
              <div className="chapter-editor__grid chapter-editor__grid--content">
            <FormField
              control={contentForm.control}
              name="contentType"
              render={({ field }) => (
                <FormItem className="chapter-editor__field">
                  <FormLabel>Innehållstyp</FormLabel>
                  <FormControl>
                    <select
                      value={String(field.value)}
                      onChange={(e) => field.onChange(Number(e.target.value) as ContentType)}
                    >
                      <option value={ContentType.Text}>Text</option>
                      <option value={ContentType.Video}>Video</option>
                      <option value={ContentType.Attachment}>Dokument</option>
                      <option value={ContentType.Exercise}>Övning</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={contentForm.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem className="chapter-editor__field chapter-editor__field--small">
                  <FormLabel>Ordning</FormLabel>
                  <FormControl>
                    <input type="number" min={1} {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={contentForm.control}
            name="textContent"
            render={({ field }) => (
              <FormItem className="chapter-editor__field">
                <FormLabel>Textinnehåll / anteckning</FormLabel>
                {watchedContentType === ContentType.Text || watchedContentType === ContentType.Exercise ? (
                  <div className="chapter-editor__richtext-shell">
                    <CKEditorFull value={field.value ?? ""} onChange={field.onChange} />
                  </div>
                ) : (
                  <FormControl>
                    <textarea placeholder="Skriv text eller en kort beskrivning" {...field} />
                  </FormControl>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          {(watchedContentType === ContentType.Text || watchedContentType === ContentType.Exercise) && (
            <div className="chapter-editor__preview-card">
              <h4>Förhandsvisning</h4>
              <p>Så här kommer innehållet att visas i medlemsportalen.</p>
              <div
                className="chapter-editor__preview-body"
                dangerouslySetInnerHTML={{ __html: textContentPreview || "<p>Ingen text än.</p>" }}
              />
            </div>
          )}

          <div className="chapter-editor__content-help">
            {editingContent ? (
              <>
                <p>Nuvarande typ: {toMediaLabel(editingContent.contentType)}</p>
                {(watchedContentType === ContentType.Video || watchedContentType === ContentType.Attachment) && (
                  <FileUpload
                    resetToken={contentUploadResetToken}
                    onVideoUploaded={handleEditVideoUploaded}
                    onDocumentUploaded={handleEditDocumentUploaded}
                    accept={watchedContentType === ContentType.Video ? "video/*" : "application/pdf,image/*"}
                    disabled={isSavingContent}
                  />
                )}
                <div className="chapter-editor__actions">
                  <Button
                    text={isSavingContent ? "Sparar innehåll..." : "Spara innehåll"}
                    className="submit-button"
                    disabled={isSavingContent}
                  />
                </div>
                {contentForm.formState.isDirty && (
                  <p className="chapter-editor__dirty-warning">Du har osparade ändringar i innehållet.</p>
                )}
              </>
            ) : (
              <>
                {(watchedContentType === ContentType.Video || watchedContentType === ContentType.Attachment) && (
                  <FileUpload
                    resetToken={chapterUploadResetToken}
                    onVideoUploaded={handleCreateVideoUploaded}
                    onDocumentUploaded={handleCreateDocumentUploaded}
                    accept={watchedContentType === ContentType.Video ? "video/*" : "application/pdf,image/*"}
                    disabled={isSavingContent}
                  />
                )}

                {(watchedContentType === ContentType.Text || watchedContentType === ContentType.Exercise) && (
                  <div className="chapter-editor__actions">
                    <Button
                      text={isSavingContent ? "Sparar innehåll..." : "Spara innehåll"}
                      className="submit-button"
                      disabled={isSavingContent}
                    />
                  </div>
                )}
                {contentForm.formState.isDirty && (
                  <p className="chapter-editor__dirty-warning">Du har osparade ändringar i innehållet.</p>
                )}
              </>
            )}
          </div>
        </form>
      </FormProvider>
    </section>
  );

  return (
    <div className="chapter-editor">
      <section className="chapter-editor__create-card">
        <div className="chapter-editor__section-head">
          <div>
            <h3>{editingChapter ? "Redigera kapitel" : "Lägg till kapitel"}</h3>
            <p>{editingChapter ? "Uppdatera titeln, beskrivningen, ordningen eller rollen" : "Skapa ett nytt kapitel i kursen"}</p>
          </div>

          {editingChapter && (
            <Button
              text="Avbryt redigering"
              className="chapter-editor__secondary-btn"
              onClick={() => setEditingChapterId(null)}
            />
          )}
        </div>

        <FormProvider {...chapterForm}>
          <form onSubmit={chapterForm.handleSubmit(saveChapter)} className="chapter-editor__form">
            <div className="chapter-editor__grid">
              <FormField
                control={chapterForm.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="chapter-editor__field">
                    <FormLabel>Kapiteltitel</FormLabel>
                    <FormControl>
                      <input placeholder="T.ex. Introduktion till andning" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={chapterForm.control}
                name="sortOrder"
                render={({ field }) => (
                  <FormItem className="chapter-editor__field chapter-editor__field--small">
                    <FormLabel>Ordning</FormLabel>
                    <FormControl>
                      <input
                        type="number"
                        min={1}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={chapterForm.control}
                name="chapterRole"
                render={({ field }) => (
                  <FormItem className="chapter-editor__field">
                    <FormLabel>Kapitelroll</FormLabel>
                    <FormControl>
                      <select value={String(field.value)} onChange={(e) => field.onChange(Number(e.target.value) as ChapterRole)}>
                        <option value={ChapterRole.Normal}>Standard</option>
                        <option value={ChapterRole.Prep}>Uppstartsmodul</option>
                        <option value={ChapterRole.Main}>Huvuddel</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={chapterForm.control}
              name="description"
              render={({ field }) => (
                <FormItem className="chapter-editor__field">
                  <FormLabel>Beskrivning</FormLabel>
                  <FormControl>
                    <textarea placeholder="Kort beskrivning av kapitlet" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="chapter-editor__actions">
              <Button
                text={isSavingChapter ? "Sparar kapitel..." : editingChapter ? "Spara kapitel" : "Lägg till kapitel"}
                className="submit-button"
                disabled={isSavingChapter}
              />
            </div>

            {chapterForm.formState.isDirty && (
              <p className="chapter-editor__dirty-warning">Du har osparade ändringar i kapitelformuläret.</p>
            )}
          </form>
        </FormProvider>
      </section>

      <section className="chapter-editor__list-card">
        <div className="chapter-editor__section-head">
          <div>
            <h3>Kapitel</h3>
            <p>Hantera befintliga kapitel och välj vilket kapitel som ska få innehåll</p>
          </div>
        </div>

        <div className="chapter-editor__chapter-list">
          {sortedChapters.map((chapter, index) => (
            <article
              key={chapter.id}
              ref={selectedChapterId === chapter.id ? activeChapterCardRef : undefined}
              className={`chapter-editor__chapter-card ${selectedChapterId === chapter.id ? "chapter-editor__chapter-card--active" : ""}`}
            >
              <div className="chapter-editor__chapter-summary">
                <button
                  type="button"
                  className="chapter-editor__chapter-summary-main"
                  onClick={() => openChapter(chapter.id)}
                >
                  <div className="chapter-editor__chapter-badge">Kapitel {index + 1}</div>
                  <div className="chapter-editor__chapter-role-badge">{getChapterRoleLabel(chapter.chapterRole)}</div>
                  <h4>{chapter.title}</h4>
                  <p>{chapter.description}</p>
                  <div className="chapter-editor__chapter-meta">
                    <span>Ordning {chapter.sortOrder}</span>
                    <span>{chapter.contents.length} innehåll</span>
                  </div>
                </button>

                <div className="chapter-editor__chapter-actions chapter-editor__chapter-actions--summary">
                  <Button
                    text={selectedChapterId === chapter.id ? "Stäng" : "Öppna"}
                    className="chapter-editor__ghost-btn"
                    type="button"
                    onClick={() => openChapter(chapter.id)}
                  />
                  <Button
                    text="Redigera"
                    className="chapter-editor__ghost-btn"
                    type="button"
                    onClick={() => {
                      if (!confirmDiscardChanges()) return;
                      setEditingChapterId(chapter.id);
                    }}
                  />
                  <Button
                    text="Ta bort"
                    className="chapter-editor__danger-btn"
                    type="button"
                    onClick={() => deleteChapter(chapter.id)}
                  />
                </div>
              </div>

              {selectedChapterId === chapter.id && (
                <div className="chapter-editor__chapter-body">
                  <div className="chapter-editor__content-list">
                    {chapter.contents.slice().sort((a, b) => a.sortOrder - b.sortOrder).map((content) => (
                      <div key={content.id} className="chapter-editor__content-row">
                        <div>
                          <div className="chapter-editor__content-badge">Ordning {content.sortOrder}</div>
                          <strong>{toMediaLabel(content.contentType)}</strong>
                          <p>
                            {getContentSummary(content)}
                          </p>
                        </div>

                        <div className="chapter-editor__content-actions">
                          <Button
                            text="Edit"
                            className="chapter-editor__ghost-btn"
                            type="button"
                            onClick={() => {
                              if (!confirmDiscardChanges()) return;
                              setSelectedChapterId(chapter.id);
                              setEditingContentId(content.id);
                            }}
                          />
                          <Button
                            text="Ta bort"
                            className="chapter-editor__danger-btn"
                            type="button"
                            onClick={() => deleteContent(content.id)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedChapter?.id === chapter.id && contentEditor}
                </div>
              )}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ChapterForm;
